import React, { useMemo, useState } from 'react';
import { ChevronDown, TrendingUp, TrendingDown, Activity, MessageSquare, Shield, Heart, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Scan } from '../../types/scan';
interface CommunicationStyleWidgetProps {
  scans: Scan[];
}
export function CommunicationStyleWidget({
  scans
}: CommunicationStyleWidgetProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState<{
    traitIndex: number;
    pointIndex: number;
    value: number;
    x: number;
    y: number;
  } | null>(null);
  // Helper function to safely get patterns array
  const getPatterns = (scan: Scan): any[] => {
    if (!scan.patterns) return [];
    if (Array.isArray(scan.patterns)) return scan.patterns;
    // If patterns is a string, try to parse it
    if (typeof scan.patterns === 'string') {
      try {
        const parsed = JSON.parse(scan.patterns);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    // If patterns is an object, wrap it in an array
    if (typeof scan.patterns === 'object') {
      return [scan.patterns];
    }
    return [];
  };
  // Analyze real scan data to derive communication metrics
  const communicationData = useMemo(() => {
    const completedScans = scans.filter(s => s.status === 'completed' && s.patterns);
    if (completedScans.length === 0) {
      return null;
    }
    // Map pattern types to communication traits
    const calculateTraitScore = (patternTypes: string[]) => {
      const assertivenessPatterns = ['passive-aggressive', 'indirect-communication', 'avoidance'];
      const empathyPatterns = ['dismissive', 'invalidation', 'lack-of-empathy', 'gaslighting'];
      const boundaryPatterns = ['manipulation', 'guilt-tripping', 'boundary-violation', 'control'];
      const assertiveness = 100 - patternTypes.filter(p => assertivenessPatterns.some(ap => p.toLowerCase().includes(ap))).length * 15;
      const empathy = 100 - patternTypes.filter(p => empathyPatterns.some(ep => p.toLowerCase().includes(ep))).length * 15;
      const boundarySetting = 100 - patternTypes.filter(p => boundaryPatterns.some(bp => p.toLowerCase().includes(bp))).length * 15;
      return {
        assertiveness: Math.max(20, Math.min(100, assertiveness)),
        empathy: Math.max(20, Math.min(100, empathy)),
        boundarySetting: Math.max(20, Math.min(100, boundarySetting))
      };
    };
    // Get last 5 scans for trend analysis (chronological: oldest → newest)
    const recentScans = completedScans.slice(0, 5).reverse();
    const assertivenessValues: number[] = [];
    const empathyValues: number[] = [];
    const boundaryValues: number[] = [];
    recentScans.forEach(scan => {
      const patterns = getPatterns(scan);
      const patternTypes = patterns.map(p => p.type || '');
      const scores = calculateTraitScore(patternTypes);
      assertivenessValues.push(scores.assertiveness);
      empathyValues.push(scores.empathy);
      boundaryValues.push(scores.boundarySetting);
    });
    // No synthetic padding — chart renders only actual user scan data points.
    // This avoids a misleading flat line at a single repeated value when the
    // user has fewer than 5 scans (which previously produced "always 100" looking charts).
    const currentAssertiveness = assertivenessValues[assertivenessValues.length - 1];
    const currentEmpathy = empathyValues[empathyValues.length - 1];
    const currentBoundary = boundaryValues[boundaryValues.length - 1];
    // Determine persona based on current scores
    const avgScore = (currentAssertiveness + currentEmpathy + currentBoundary) / 3;
    let persona = 'Balanced Communicator';
    if (currentAssertiveness >= 70 && currentBoundary >= 70) {
      persona = 'Assertive Communicator';
    } else if (currentEmpathy >= 75) {
      persona = 'Empathetic Communicator';
    } else if (currentBoundary >= 70) {
      persona = 'Boundary-Conscious Communicator';
    } else if (avgScore < 50) {
      persona = 'Developing Communicator';
    }
    // Calculate changes from previous scan
    const changes: any[] = [];
    if (recentScans.length >= 2) {
      const prevAssertiveness = assertivenessValues[assertivenessValues.length - 2];
      const prevEmpathy = empathyValues[empathyValues.length - 2];
      const prevBoundary = boundaryValues[boundaryValues.length - 2];
      const assertivenessChange = currentAssertiveness - prevAssertiveness;
      const empathyChange = currentEmpathy - prevEmpathy;
      const boundaryChange = currentBoundary - prevBoundary;
      if (Math.abs(assertivenessChange) >= 5) {
        changes.push({
          trait: 'Assertiveness',
          change: Math.abs(Math.round(assertivenessChange)),
          direction: assertivenessChange > 0 ? 'up' : 'down',
          example: assertivenessChange > 0 ? 'More direct communication patterns detected in recent conversations' : 'Less assertive language patterns observed',
          scanRef: `#${completedScans[0].id.slice(0, 6)}`
        });
      }
      if (Math.abs(empathyChange) >= 5) {
        changes.push({
          trait: 'Empathy',
          change: Math.abs(Math.round(empathyChange)),
          direction: empathyChange > 0 ? 'up' : 'down',
          example: empathyChange > 0 ? 'More active listening and validation markers detected' : 'Fewer empathetic response patterns observed',
          scanRef: `#${completedScans[0].id.slice(0, 6)}`
        });
      }
      if (Math.abs(boundaryChange) >= 5) {
        changes.push({
          trait: 'Boundary-Setting',
          change: Math.abs(Math.round(boundaryChange)),
          direction: boundaryChange > 0 ? 'up' : 'down',
          example: boundaryChange > 0 ? 'Clearer boundary-setting language detected in responses' : 'Less defined boundary communication observed',
          scanRef: `#${completedScans[0].id.slice(0, 6)}`
        });
      }
    }
    // Check if persona recently evolved (comparing first and last scan)
    const firstPatterns = getPatterns(recentScans[0]);
    const lastPatterns = getPatterns(recentScans[recentScans.length - 1]);
    const firstScores = calculateTraitScore(firstPatterns.map(p => p.type || ''));
    const lastScores = calculateTraitScore(lastPatterns.map(p => p.type || ''));
    const avgChange = Math.abs((lastScores.assertiveness - firstScores.assertiveness + (lastScores.empathy - firstScores.empathy) + (lastScores.boundarySetting - firstScores.boundarySetting)) / 3);
    const recentlyEvolved = avgChange >= 15;
    return {
      currentPersona: persona,
      confidence: Math.round(avgScore),
      recentlyEvolved,
      traits: [{
        name: 'Assertiveness',
        values: assertivenessValues,
        color: '#DC2626',
        icon: MessageSquare,
        currentValue: Math.round(currentAssertiveness)
      }, {
        name: 'Empathy',
        values: empathyValues,
        color: '#10B981',
        icon: Heart,
        currentValue: Math.round(currentEmpathy)
      }, {
        name: 'Boundary-Setting',
        values: boundaryValues,
        color: '#9333EA',
        icon: Shield,
        currentValue: Math.round(currentBoundary)
      }],
      recentChanges: changes,
      scanCount: completedScans.length
    };
  }, [scans]);
  // If no data available, show empty state
  if (!communicationData) {
    return <div className="relative bg-[#0a0a0a] border border-gray-800 overflow-hidden mb-8" data-id="element-752" data-mp="1at3vtqf7dvna">
        <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-gray-600" data-id="element-753" data-mp="lo41lot6qjwx" />
        <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-gray-600" data-id="element-754" data-mp="gq2emssn20g6" />
        <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-gray-600" data-id="element-755" data-mp="uptoqig1awow" />
        <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-gray-600" data-id="element-756" data-mp="qp9sri775z4c" />

        <div className="p-8 text-center" data-id="element-757" data-mp="2zp88uveatsy">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF0000]/10 border border-[#FF0000] mb-4" data-id="element-758" data-mp="3u5gydu96a6fr">
            <Activity className="w-8 h-8 text-[#FF0000]" data-id="element-759" data-mp="brr6cmhzry02" />
          </div>
          <h3 className="text-lg font-black text-white uppercase tracking-tight mb-2" data-id="element-760" data-mp="70sq1mx06zwt">
            Communication Forensics
          </h3>
          <p className="text-gray-400 font-mono text-sm mb-6" data-id="element-761" data-mp="3ffj4cxokamin">
            Initialize first scan to generate behavioral communication profile
          </p>
        </div>
      </div>;
  }
  // Chart dimensions
  const width = 100;
  const height = 40;
  const padding = 2;
  // Helper to scale values to SVG coordinates
  // Guard: when only 1 data point exists, center it horizontally to avoid NaN.
  const getX = (index: number, total: number) => total <= 1 ? width / 2 : index / (total - 1) * (width - padding * 2) + padding;
  const getY = (value: number) => height - value / 100 * (height - padding * 2) - padding;
  // Generate path string for a line
  const getPath = (values: number[]) => {
    return values.map((val, i) => `${i === 0 ? 'M' : 'L'} ${getX(i, values.length)} ${getY(val)}`).join(' ');
  };
  const topChange = communicationData.recentChanges[0];
  return <div className="relative bg-[#0a0a0a] border border-gray-800 overflow-hidden mb-8" data-id="element-762" data-mp="3f4jus7seocze">
      <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-gray-600" data-id="element-763" data-mp="1ymw0xwk7dyww" />
      <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-gray-600" data-id="element-764" data-mp="23ov5vavld3vy" />
      <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-gray-600" data-id="element-765" data-mp="38vnjshlkn72i" />
      <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-gray-600" data-id="element-766" data-mp="3dsx81tx85cor" />

      {/* PRIMARY: Current Persona - Hero Section */}
      <div className="p-6 space-y-4 border-b border-gray-800" data-id="element-767" data-mp="3445gvnzzmwew">
        <div className="flex items-start justify-between gap-4" data-id="element-768" data-mp="342m3c55puzgt">
          <div className="flex-1 space-y-2" data-id="element-769" data-mp="2q3maelb67yeu">
            {/* Label - Tertiary */}
            <div className="flex items-center gap-2" data-id="element-770" data-mp="2v4a45eqsatcr">
              <Activity className="w-4 h-4 text-[#FF0000]" data-id="element-771" data-mp="2v1nvtcw2k9th" />
              <span className="text-sm font-mono text-gray-500 uppercase tracking-widest leading-tight" data-id="element-772" data-mp="2g7jalm3skbv2">
                Subject Communication Profile
              </span>
            </div>

            {/* Persona Title - PRIMARY (text-2xl font-bold) */}
            <h2 className="text-2xl font-black text-white uppercase tracking-tight leading-tight" data-id="element-773" data-mp="je7pjk72q7bj">
              {communicationData.currentPersona}
            </h2>

            {/* Metadata - Tertiary (text-sm text-gray-500) */}
            <div className="flex items-center gap-2 text-sm font-mono text-gray-400 leading-snug" data-id="element-774" data-mp="1yxx7pb5ht9nq">
              <span className="font-medium text-[#FF0000]" data-id="element-775" data-mp="2dmbic895i2eu">
                {communicationData.confidence}% confidence
              </span>
              <span className="w-1 h-1 bg-gray-600" data-id="element-776" data-mp="28u0lje9piwqi" />
              <span data-id="element-777" data-mp="2mlymv2x6foc4">Based on {communicationData.scanCount} scans</span>
            </div>
          </div>

          {/* Evolution Badge - Visual Cue */}
          {communicationData.recentlyEvolved && <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF0000]/10 border border-[#FF0000] text-[#FF0000] text-xs font-mono uppercase tracking-widest" data-id="element-778" data-mp="2wl60zsbqjn0a">
              <Sparkles className="w-3.5 h-3.5" data-id="element-779" data-mp="2xat80kkqzypf" />
              <span data-id="element-780" data-mp="2xc4bske716gj">Recently Evolved</span>
            </motion.div>}
        </div>

        {/* Latest Change Preview - Above Fold */}
        {topChange && <div className={`p-4 border space-y-2 ${topChange.direction === 'up' ? 'bg-green-500/5 border-green-500/20' : 'bg-orange-500/5 border-orange-500/20'}`} data-id="element-781" data-mp="369y5dnkkpm00">
            <div className="flex items-center gap-2" data-id="element-782" data-mp="3g7uksrkjn33x">
              <span className="text-sm font-bold text-white uppercase tracking-wider leading-tight" data-id="element-783" data-mp="3l80eseead1p6">
                Latest Detection:
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-mono uppercase tracking-widest ${topChange.direction === 'up' ? 'bg-green-500/10 text-green-500 border-green-500/30' : 'bg-orange-500/10 text-orange-500 border-orange-500/30'}`} data-id="element-784" data-mp="3pz8o7afm8n2c">
                {topChange.direction === 'up' ? <TrendingUp className="w-3 h-3" data-id="element-785" data-mp="hxk5yzm5qkzq" /> : <TrendingDown className="w-3 h-3" data-id="element-786" data-mp="huoadhxs88eg" />}
                {topChange.trait} {topChange.direction === 'up' ? '+' : '-'}
                {topChange.change}%
              </span>
            </div>
            {/* Description - Secondary (text-base text-gray-600) */}
            <p className="text-sm font-mono text-gray-400 leading-relaxed" data-id="element-787" data-mp="3zm58x0ren2p">
              {topChange.example}
            </p>
          </div>}
      </div>

      {/* SECONDARY: Trait Scores Section */}
      <div className="p-6 space-y-4 border-b border-gray-800" data-id="element-788" data-mp="vqs0az1mhul4">
        {/* Section Label - Tertiary */}
        <h3 className="text-[10px] font-mono text-gray-500 uppercase tracking-widest leading-tight" data-id="element-789" data-mp="mw3rx00r3ws3">
          Behavioral Markers
        </h3>

        {/* Trait Cards - 8px Grid Aligned */}
        <div className="grid grid-cols-3 gap-4" data-id="element-790" data-mp="1jfuroym271st">
          {communicationData.traits.map(trait => {
          const Icon = trait.icon;
          return <div key={trait.name} className="text-center space-y-2" data-id="element-791" data-mp="z3p5g7zp49pi">
                <div className="w-12 h-12 mx-auto flex items-center justify-center border" style={{
              '--trait-color': trait.color,
              '--trait-color-15': `${trait.color}15`,
              '--trait-color-50': `${trait.color}50`,
              backgroundColor: 'var(--trait-color-15)',
              borderColor: 'var(--trait-color-50)'
            } as React.CSSProperties} data-id="element-792" data-mp="144mwla2ooe35">
                  <Icon className="w-5 h-5" style={{
                color: trait.color
              }} data-id="element-793" data-mp="1937omm7s7rgi" />
                </div>
                {/* Score - Primary for this context */}
                <div className="text-2xl font-black text-white font-mono leading-none" data-id="element-794" data-mp="25llzq34hdil2">
                  {trait.currentValue}
                </div>
                {/* Label - Tertiary */}
                <div className="text-xs font-mono text-gray-500 uppercase tracking-widest leading-tight" data-id="element-795" data-mp="3ld7uwqdi8tsd">
                  {trait.name}
                </div>
              </div>;
        })}
        </div>

        {/* Chart Section */}
        <div className="space-y-4 pt-2" data-id="element-796" data-mp="3v39h1cmas24n">
          <div className="flex items-center justify-between" data-id="element-797" data-mp="37cn87j4gydtl">
            <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-798" data-mp="37d5bv0tgnubj">
              Trend Analysis
            </span>

            {/* Legend */}
            <div className="flex gap-3" data-id="element-799" data-mp="3hc4g877713sq">
              {communicationData.traits.map(trait => <div key={trait.name} className="flex items-center gap-1.5 text-[10px] font-mono text-gray-400 uppercase tracking-widest" data-id="element-800" data-mp="3cb6whq6jc1p4">
                  <span className="w-2 h-2" style={{
                backgroundColor: trait.color
              }} data-id="element-801" data-mp="rkpoe8zpiu9b" />
                  <span className="hidden sm:inline" data-id="element-802" data-mp="13n91u5rw5nvc">{trait.name}</span>
                </div>)}
            </div>
          </div>

          {/* SVG Chart */}
          <div className="relative h-32 w-full" data-id="element-803" data-mp="1dm80cviy034m">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full overflow-visible" preserveAspectRatio="none" data-id="element-804" data-mp="1dkx1990fzjju">
              {/* Grid Lines - More Subtle */}
              {[0, 25, 50, 75, 100].map(tick => <line key={tick} x1="0" y1={getY(tick)} x2="100" y2={getY(tick)} stroke="currentColor" strokeOpacity="0.2" strokeWidth="0.2" className="text-gray-800" data-id="element-805" data-mp="pwdnwf2jcgkw" />)}

              {/* Data Lines */}
              {communicationData.traits.map((trait, traitIndex) => <g key={trait.name} data-id="element-806" data-mp="2bfr61k17mje0" data-mpi={traitIndex}>
                  <path d={getPath(trait.values)} fill="none" stroke={trait.color} strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="miter" data-id="element-807" data-mp="2bgsm62x31w2l" />

                  {/* Interaction Points */}
                  {trait.values.map((val, pointIndex) => <circle key={pointIndex} cx={getX(pointIndex, trait.values.length)} cy={getY(val)} r="2" fill={trait.color} stroke="#0a0a0a" strokeWidth="0.5" className="cursor-pointer hover:r-3 transition-all duration-200" onMouseEnter={e => {
                const rect = e.currentTarget.getBoundingClientRect();
                setHoveredPoint({
                  traitIndex,
                  pointIndex,
                  value: val,
                  x: rect.left + rect.width / 2,
                  y: rect.top
                });
              }} onMouseLeave={() => setHoveredPoint(null)} data-id="element-808" data-mp="1rqp6q7wsplvu" data-mpi={pointIndex} />)}
                </g>)}
            </svg>

            {/* Tooltip */}
            <AnimatePresence data-id="element-809" data-mp="2iqm2c1cm5uof">
              {hoveredPoint && <motion.div initial={{
              opacity: 0,
              y: 5
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 5
            }} className="fixed z-50 px-2 py-1 bg-gray-900 border border-gray-700 text-white text-xs font-mono rounded-none shadow-lg pointer-events-none transform -translate-x-1/2 -translate-y-full mt-[-8px]" style={{
              left: hoveredPoint.x,
              top: hoveredPoint.y
            }} data-id="element-810" data-mp="24yxrs2i0je9k">
                  <div className="font-bold uppercase tracking-wider leading-tight mb-1" data-id="element-811" data-mp="1lccoa1f2d9nm">
                    {communicationData.traits[hoveredPoint.traitIndex].name}
                  </div>
                  <div className="leading-tight text-gray-400" data-id="element-812" data-mp="1vblhut60om9g">
                    Score:{' '}
                    <span className="text-white" data-id="element-813" data-mp="292qcthyfwqdg">{hoveredPoint.value}/100</span>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* X-Axis Labels - Tertiary */}
          <div className="flex justify-between text-[10px] font-mono text-gray-500 uppercase tracking-widest px-1" data-id="element-814" data-mp="2j0w99k1h94ax">
            <span data-id="element-815" data-mp="2dz6y84e98qmu">
              {communicationData.traits[0].values.length === 1 ? 'First scan' : `Last ${communicationData.traits[0].values.length} scans`}
            </span>
            <span data-id="element-816" data-mp="2lnaxj06k5mz3">Latest</span>
          </div>
        </div>
      </div>

      {/* PROGRESSIVE DISCLOSURE: Full Change History */}
      {communicationData.recentChanges.length > 0 && <div data-id="element-817" data-mp="2vjw6txpm1k8q">
          <button onClick={() => setIsExpanded(!isExpanded)} className="w-full flex items-center justify-between px-6 py-4 text-sm font-bold text-gray-400 hover:text-white hover:bg-gray-900 transition-colors group uppercase tracking-wider" data-id="element-818" data-mp="2vh9yhvuwb0pg">
            <span className="flex items-center gap-2" data-id="element-819" data-mp="2gn51l7t99eb7">
              <Sparkles className="w-4 h-4 text-[#FF0000] group-hover:text-[#FF0000] transition-colors" data-id="element-820" data-mp="2buvgc53352f9" />
              View Full Behavioral Log
              <span className="text-xs font-mono text-gray-500" data-id="element-821" data-mp="1o3q3lysfyq96">
                ({communicationData.recentChanges.length} detections)
              </span>
            </span>
            <motion.div animate={{
          rotate: isExpanded ? 180 : 0
        }} transition={{
          duration: 0.3
        }} data-id="element-822" data-mp="19z8wgk6rl3gw">
              <ChevronDown className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors" data-id="element-823" data-mp="1035ubebwx4ty" />
            </motion.div>
          </button>

          <AnimatePresence data-id="element-824" data-mp="1sqij7v5jpoqf">
            {isExpanded && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-825" data-mp="1sr1ooce0eknk">
                <div className="px-6 pb-6 pt-2 space-y-3 bg-[#050505]" data-id="element-826" data-mp="ca5xtx1qh6ld">
                  {communicationData.recentChanges.map((change, i) => <div key={i} className="bg-[#0a0a0a] border border-gray-800 p-4 space-y-2" data-id="element-827" data-mp="29zksvt8qug4f" data-mpi={i}>
                      <div className="flex items-center justify-between" data-id="element-828" data-mp="256rqvd4ntlw1">
                        <div className="flex items-center gap-2" data-id="element-829" data-mp="209ri2ugfe518">
                          {/* Change Title - Secondary */}
                          <span className="text-sm font-bold text-white uppercase tracking-wider leading-tight" data-id="element-830" data-mp="1r9csvk0cc5sg">
                            {change.trait}
                          </span>
                          {/* Badge - Semantic Color */}
                          <span className={`
                            inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-mono uppercase tracking-widest
                            ${change.direction === 'up' ? 'bg-green-500/10 text-green-500 border-green-500/30' : 'bg-orange-500/10 text-orange-500 border-orange-500/30'}
                          `} data-id="element-831" data-mp="1hc70m8fzq8qn">
                            {change.direction === 'up' ? <TrendingUp className="w-3 h-3" data-id="element-832" data-mp="3sy90sup3xeqp" /> : <TrendingDown className="w-3 h-3" data-id="element-833" data-mp="1shb1wf8s44f" />}
                            {change.direction === 'up' ? '+' : '-'}
                            {change.change}%
                          </span>
                        </div>
                        {/* Metadata - Tertiary */}
                        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-834" data-mp="3a77xjugyex1c">
                          Scan {change.scanRef}
                        </span>
                      </div>
                      {/* Description - Secondary */}
                      <p className="text-sm font-mono text-gray-400 leading-relaxed" data-id="element-835" data-mp="ugdedjzbgua6">
                        {change.example}
                      </p>
                    </div>)}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>}
    </div>;
}